import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Package, Users, Globe, Award, Truck, Shield, Clock, CheckCircle, Building2, MapPin, Phone, Mail, ArrowRight } from "lucide-react";

const SobreNosotros = () => {
  const navigate = useNavigate();

  const stats = [
    { number: "15+", label: "Años de experiencia" },
    { number: "50M+", label: "Envíos procesados" },
    { number: "32", label: "Países alcanzados" },
    { number: "500+", label: "Empresas confían en nosotros" },
  ];

  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Confiabilidad",
      description: "Cada envío es tratado con el máximo cuidado y profesionalismo.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Eficiencia",
      description: "Optimizamos cada proceso para entregas rápidas y puntuales.",
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Servicio al cliente",
      description: "Equipo dedicado disponible para resolver cualquier duda.",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Alcance global",
      description: "Red de distribución en más de 32 países alrededor del mundo.",
      color: "from-violet-500 to-purple-500"
    },
  ];

  const timeline = [
    { year: "2009", title: "Fundación", description: "Iniciamos operaciones como agencia de carga en Ciudad de México." },
    { year: "2012", title: "Expansión nacional", description: "Abrimos oficinas en Guadalajara, Monterrey y Tijuana." },
    { year: "2015", title: "Alianzas estratégicas", description: "Firmamos acuerdos con FedEx, DHL y UPS como partners certificados." },
    { year: "2018", title: "Transformación digital", description: "Lanzamos nuestra primera plataforma de cotización en línea." },
    { year: "2021", title: "Expansión LATAM", description: "Iniciamos operaciones en Colombia, Chile y Perú." },
    { year: "2024", title: "SuperPack.mx", description: "Lanzamos SuperPack, el comparador de envíos más completo de México." },
  ];

  const partners = [
    "FedEx", "DHL", "UPS", "Estafeta", "Redpack", "99 Minutos", "Paquetexpress"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-lg border-b border-zinc-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Package className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-['Outfit']">
                <span className="text-violet-600">Super</span>
                <span className="text-zinc-800">Pack</span>
              </span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="/" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Inicio</a>
              <a href="/sobre-nosotros" className="text-violet-600 font-medium">Sobre Nosotros</a>
              <a href="/contacto" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Contacto</a>
            </nav>

            <Button 
              onClick={() => navigate("/register")}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6"
            >
              Comenzar
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-24 px-4 bg-gradient-to-br from-violet-600 via-purple-600 to-violet-700 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative text-center text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] mb-6">
            Grupo Logístico Internacional
          </h1>
          <p className="text-xl text-violet-100 max-w-3xl mx-auto mb-8">
            Con más de 15 años de experiencia conectando México con el mundo. 
            Somos expertos en logística y distribución de paquetería.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-lg rounded-2xl px-8 py-6 border border-white/20">
                <p className="text-4xl font-bold">{stat.number}</p>
                <p className="text-violet-200 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nuestra Historia */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">Nuestra Historia</span>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mt-2 mb-6">
                Más de una década moviendo México
              </h2>
              <div className="space-y-4 text-zinc-600">
                <p>
                  <strong>SuperPack</strong> nació de la visión de un grupo de profesionales con más de 15 años 
                  de experiencia en el sector logístico internacional. Fundado en 2009 como una pequeña agencia 
                  de carga en Ciudad de México, hoy somos uno de los principales agregadores de servicios de 
                  paquetería en el país.
                </p>
                <p>
                  Nuestra trayectoria nos ha permitido establecer <strong>alianzas estratégicas</strong> con las 
                  principales paqueterías del mundo: FedEx, DHL, UPS, Estafeta, Redpack y muchas más. Estas 
                  relaciones nos permiten ofrecer a nuestros clientes los mejores precios del mercado.
                </p>
                <p>
                  Hemos procesado más de <strong>50 millones de envíos</strong> para empresas de todos los tamaños, 
                  desde emprendedores hasta corporativos multinacionales. Nuestra misión es democratizar el acceso 
                  a servicios logísticos de calidad.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600"
                alt="Logistics warehouse"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-violet-600 to-purple-600 text-white p-6 rounded-2xl shadow-xl">
                <Building2 className="w-8 h-8 mb-2" />
                <p className="font-bold text-2xl">5 oficinas</p>
                <p className="text-violet-200 text-sm">en México</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-4 bg-zinc-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">Trayectoria</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mt-2">
              Nuestra evolución
            </h2>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-violet-200"></div>
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? '' : 'flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <Card className="inline-block">
                      <CardContent className="p-6">
                        <span className="text-violet-600 font-bold text-xl">{item.year}</span>
                        <h3 className="font-bold text-lg mt-1">{item.title}</h3>
                        <p className="text-zinc-500 text-sm mt-2">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="w-4 h-4 bg-violet-600 rounded-full border-4 border-white shadow-lg z-10"></div>
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">Valores</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mt-2">
              Lo que nos define
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center text-white mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    {value.icon}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{value.title}</h3>
                  <p className="text-zinc-500 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 px-4 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-4">
            Nuestros Partners
          </h2>
          <p className="text-zinc-400 mb-12 max-w-2xl mx-auto">
            Trabajamos con las mejores paqueterías del mundo para garantizar entregas seguras y puntuales.
          </p>
          
          <div className="flex flex-wrap justify-center gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-lg rounded-xl px-8 py-4 border border-white/20">
                <span className="font-bold text-lg">{partner}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-4">
            ¿Listo para trabajar con expertos?
          </h2>
          <p className="text-zinc-600 mb-8 text-lg">
            Únete a las más de 500 empresas que confían en SuperPack para sus envíos.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={() => navigate("/register")}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-8 py-6 text-lg"
            >
              Crear cuenta gratis <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/contacto")}
              className="px-8 py-6 text-lg"
            >
              Contactar ventas
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-400 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className="text-white font-bold font-['Outfit'] text-xl">SuperPack</span>
          </div>
          <p className="text-sm">© 2026 SuperPack.mx - Todos los derechos reservados</p>
        </div>
      </footer>
    </div>
  );
};

export default SobreNosotros;
